import { useState } from "react";
import { Mail, MessageSquare, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { apiErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import { PublicNav } from "../components/PublicNav";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const inputStyle = { background: "#1c1c1c", border: "1px solid #2d2d2d" };
const focus = (e) => (e.target.style.borderColor = "#7c3aed");
const blur = (e) => (e.target.style.borderColor = "#2d2d2d");

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errors.required"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t("contact.toasts.sent"));
    } catch (err) {
      const msg = apiErrorMessage(err, t("contact.errors.failed"));
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const infoCards = [
    { icon: Mail, title: t("contact.cards.email"), value: "info@relaxedlifestyler.com" },
    { icon: MessageSquare, title: t("contact.cards.support"), value: t("contact.cards.supportValue") },
    { icon: Clock, title: t("contact.cards.response"), value: t("contact.cards.responseValue") },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#111111" }}>
      <PublicNav testIdPrefix="contact" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#7c3aed" }}>{t("contact.eyebrow")}</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: "Epilogue, sans-serif" }}>
            {t("contact.titleLine1")}<br />{t("contact.titleLine2")} <span style={{ color: "#c084fc" }}>{t("contact.titleAccent")}</span>
          </h1>
          <p className="text-base" style={{ color: "#6b6b6b" }}>{t("contact.intro")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {infoCards.map(({ icon: Icon, title, value }) => (
            <div key={title} className="rounded-2xl p-5" style={{ background: "#161616", border: "1px solid #252525" }}>
              <Icon className="w-4 h-4 mb-2" style={{ color: "#7c3aed" }} />
              <p className="text-xs font-bold text-white mb-1">{title}</p>
              <p className="text-xs" style={{ color: "#6b6b6b" }}>{value}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-8" style={{ background: "#161616", border: "1px solid #252525" }}>
          {sent ? (
            <div className="py-12 text-center" data-testid="contact-success">
              <CheckCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "#34d399" }} />
              <p className="text-lg font-bold text-white mb-2">{t("contact.success.title")}</p>
              <p className="text-sm" style={{ color: "#6b6b6b" }}>
                {t("contact.success.bodyBefore")} <span style={{ color: "#c084fc" }}>{form.email}</span> {t("contact.success.bodyAfter")}
              </p>
              <button
                data-testid="contact-send-another-btn"
                onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                className="mt-6 text-sm px-4 py-2 rounded-xl transition-colors"
                style={{ background: "#252525", color: "#a0a0a0" }}>
                {t("contact.success.another")}
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-white mb-6">{t("contact.form.title")}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "#6b6b6b" }}>{t("contact.form.name")} *</label>
                    <input data-testid="contact-name" type="text" placeholder={t("contact.form.namePlaceholder")}
                      value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-colors"
                      style={inputStyle} onFocus={focus} onBlur={blur} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "#6b6b6b" }}>{t("contact.form.email")} *</label>
                    <input data-testid="contact-email" type="email" placeholder="you@company.com"
                      value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-colors"
                      style={inputStyle} onFocus={focus} onBlur={blur} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "#6b6b6b" }}>{t("contact.form.subject")}</label>
                  <input data-testid="contact-subject" type="text" placeholder={t("contact.form.subjectPlaceholder")}
                    value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-colors"
                    style={inputStyle} onFocus={focus} onBlur={blur} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "#6b6b6b" }}>{t("contact.form.message")} *</label>
                  <textarea data-testid="contact-message" rows={5} placeholder={t("contact.form.messagePlaceholder")}
                    value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-colors resize-none"
                    style={inputStyle} onFocus={focus} onBlur={blur} />
                </div>
                <button type="submit" data-testid="contact-submit-btn" disabled={sending}
                  className="w-full py-3 rounded-xl font-bold text-sm transition-all text-white flex items-center justify-center gap-2"
                  style={{ background: sending ? "#4a3f6b" : "#7c3aed", cursor: sending ? "not-allowed" : "pointer" }}>
                  {sending ? t("contact.form.sending") : t("contact.form.send")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
